import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { eventsApi, formatDate } from "../lib/api";
import { Plus, Calendar, Users, Image, LogOut, Camera, QrCode, ChevronRight, Loader2, Settings, ShieldAlert, Tv } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import HostSettingsDialog from "../components/HostSettingsDialog";
import ChangePasswordDialog from "../components/ChangePasswordDialog";
import ConnectTVDialog from "../components/ConnectTVDialog";

export default function HostDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsEvent, setSettingsEvent] = useState(null); // { id, name }
  const [tvDialogOpen, setTvDialogOpen] = useState(false);
  const [newEvent, setNewEvent] = useState({ name: "", date: "", description: "" });

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const { data } = await eventsApi.list();
      setEvents(data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await eventsApi.create(newEvent);
      setNewEvent({ name: "", date: "", description: "" });
      setDialogOpen(false);
      loadEvents();
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-[#FFFBF7]" data-testid="host-dashboard">
      {/* Header */}
      <header className="sticky top-0 w-full bg-[#FFFBF7]/80 backdrop-blur-xl border-b-2 border-[#1C1917] z-40">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[#FF6B6B] border-2 border-[#1C1917] rounded-lg flex items-center justify-center shadow-[2px_2px_0px_#1C1917]">
              <Camera className="w-4 h-4 text-white" strokeWidth={2.5} />
            </div>
            <span className="font-black text-lg tracking-tight text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>Festa.IA</span>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-[#44403C] hidden sm:block">{user?.name || user?.email}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTvDialogOpen(true)}
              className="border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917] gap-1.5"
              data-testid="connect-tv-btn"
            >
              <Tv className="w-4 h-4" strokeWidth={2.5} />
              <span className="hidden sm:inline text-xs font-bold">Conectar TV</span>
            </Button>
            <ChangePasswordDialog />
            <Button
              variant="outline"
              size="sm"
              onClick={handleLogout}
              className="border-2 border-[#1C1917] rounded-full shadow-[2px_2px_0px_#1C1917]"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4" strokeWidth={2.5} />
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-5xl mx-auto px-4 py-8">
        {/* Title + Create */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-[#1C1917]" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Meus Eventos
            </h1>
            <p className="text-sm text-[#78716C] mt-1">Gerencie seus eventos e compartilhe QR codes</p>
          </div>
          <div className="flex gap-2">
            {user?.role === "admin" && (
              <Button
                onClick={() => navigate("/admin")}
                className="bg-[#1C1917] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#FF6B6B] rounded-full font-bold px-5 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#FF6B6B] transition-all"
                data-testid="admin-panel-btn"
              >
                <ShieldAlert className="w-5 h-5 mr-1" strokeWidth={2.5} /> Painel
              </Button>
            )}
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button
                  className="bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold px-5 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                  data-testid="create-event-btn"
                >
                  <Plus className="w-5 h-5 mr-1" strokeWidth={2.5} /> Novo Evento
                </Button>
              </DialogTrigger>
            <DialogContent className="border-2 border-[#1C1917] shadow-[6px_6px_0px_#1C1917] rounded-xl">
              <DialogHeader>
                <DialogTitle className="text-xl font-black" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                  Criar Evento
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreate} className="space-y-4 mt-2">
                <div>
                  <Label className="text-sm font-bold">Nome do Evento</Label>
                  <Input
                    value={newEvent.name}
                    onChange={(e) => setNewEvent({ ...newEvent, name: e.target.value })}
                    placeholder="Aniversario do Pedro"
                    required
                    className="mt-1 border-2 border-[#1C1917] rounded-lg"
                    data-testid="event-name-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold">Data</Label>
                  <Input
                    type="date"
                    value={newEvent.date}
                    onChange={(e) => setNewEvent({ ...newEvent, date: e.target.value })}
                    required
                    className="mt-1 border-2 border-[#1C1917] rounded-lg"
                    data-testid="event-date-input"
                  />
                </div>
                <div>
                  <Label className="text-sm font-bold">Descricao (opcional)</Label>
                  <Input
                    value={newEvent.description}
                    onChange={(e) => setNewEvent({ ...newEvent, description: e.target.value })}
                    placeholder="Detalhes sobre o evento"
                    className="mt-1 border-2 border-[#1C1917] rounded-lg"
                    data-testid="event-description-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={creating}
                  className="w-full bg-[#FF6B6B] text-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-full font-bold py-3 hover:translate-y-[2px] hover:shadow-[2px_2px_0px_#1C1917] transition-all"
                  data-testid="create-event-submit-btn"
                >
                  {creating ? <Loader2 className="w-5 h-5 animate-spin" /> : "Criar Evento"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        {/* Events List */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-8 h-8 animate-spin text-[#FF6B6B]" />
          </div>
        ) : events.length === 0 ? (
          <div className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl p-12 text-center">
            <div className="w-16 h-16 bg-[#FEE2E2] border-2 border-[#1C1917] rounded-xl flex items-center justify-center mx-auto mb-4 shadow-[2px_2px_0px_#1C1917]">
              <Calendar className="w-8 h-8 text-[#FF6B6B]" strokeWidth={2.5} />
            </div>
            <h3 className="font-bold text-[#1C1917] mb-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Nenhum evento ainda
            </h3>
            <p className="text-sm text-[#78716C]">Crie seu primeiro evento e compartilhe o QR Code</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {events.map((event) => (
              <div
                key={event.event_id}
                className="bg-white border-2 border-[#1C1917] shadow-[4px_4px_0px_#1C1917] rounded-xl overflow-hidden hover:-translate-y-1 hover:shadow-[6px_6px_0px_#1C1917] transition-all duration-200 cursor-pointer"
                onClick={() => navigate(`/event/${event.event_id}`)}
                data-testid={`event-card-${event.event_id}`}
              >
                <div className="p-5">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="font-bold text-[#1C1917] text-lg truncate pr-2" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
                      {event.name}
                    </h3>
                    <span className={`shrink-0 text-xs font-bold uppercase px-2 py-0.5 rounded-full border-2 border-[#1C1917] ${event.is_active ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}`}>
                      {event.is_active ? "Ativo" : "Inativo"}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-[#78716C]">
                    <span className="flex items-center gap-1"><Calendar className="w-4 h-4" strokeWidth={2.5} />{formatDate(event.date)}</span>
                    <span className="flex items-center gap-1"><Users className="w-4 h-4" strokeWidth={2.5} />{event.guest_count || 0}</span>
                    <span className="flex items-center gap-1"><Image className="w-4 h-4" strokeWidth={2.5} />{event.media_count || 0}</span>
                  </div>
                  <div className="flex items-center justify-between mt-3 gap-2">
                    <span className="flex items-center text-[#FF6B6B] font-bold text-sm">
                      Gerenciar <ChevronRight className="w-4 h-4 ml-1" />
                    </span>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSettingsEvent({ id: event.event_id, name: event.name });
                        setSettingsOpen(true);
                      }}
                      className="flex items-center gap-1 text-xs font-bold text-[#1C1917] bg-white border-2 border-[#1C1917] rounded-full px-3 py-1 shadow-[2px_2px_0px_#1C1917] hover:translate-y-[1px] hover:shadow-[1px_1px_0px_#1C1917] transition-all"
                      data-testid={`event-settings-btn-${event.event_id}`}
                      title="Configuracoes deste evento"
                    >
                      <Settings className="w-3.5 h-3.5" strokeWidth={2.5} />
                      Configurar
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <HostSettingsDialog
        open={settingsOpen}
        onOpenChange={(v) => {
          setSettingsOpen(v);
          if (!v) setSettingsEvent(null);
        }}
        eventId={settingsEvent?.id}
        eventName={settingsEvent?.name}
      />
      <ConnectTVDialog
        open={tvDialogOpen}
        onOpenChange={setTvDialogOpen}
        events={events}
      />
    </div>
  );
}
